import React, { useState, useEffect } from 'react';

/**
 * TextInput - Standard text input field
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {string} props.value - Current text value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.validationRegex - Regex pattern for validation
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const TextInput = ({ 
    feature, 
    value, 
    onChange,
    placeholder = 'Enter text...',
    validationRegex,
    disabled = false, 
    className = ''
}) => {
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);
    const displayText = feature?.property?.displayText || feature?.name || 'Text Input';
    
    const handleChange = (e) => {
        onChange(e);
        // Validate on change if touched
        if (touched) {
            validateValue(e.target.value);
        }
    };
    
    const handleBlur = (e) => {
        setTouched(true);
        validateValue(e.target.value);
    };
    
    const validateValue = (val) => {
        if (!validationRegex) {
            setError('');
            return;
        }
        
        try {
            const regex = new RegExp(validationRegex);
            if (!regex.test(val)) {
                setError('Invalid format');
            } else {
                setError('');
            }
        } catch (e) {
            setError('Validation error');
        }
    };
    
    return (
        <div 
            className="text-input-wrapper"
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
        >
            <label style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>{displayText}</span>
                {feature?.property?.isOptional !== false && (
                    <span style={{ color: 'var(--error)', fontSize: '0.75rem' }}>*</span>
                )}
            </label>
            <input
                className={className}
                type="text"
                value={value || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                style={{
                    border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
                    outline: 'none',
                    cursor: disabled ? 'not-allowed' : 'text',
                    opacity: disabled ? 0.6 : 1
                }}
                disabled={disabled}
            />
            {error && touched && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--error)',
                    minHeight: '1rem'
                }}>
                    {error}
                </span>
            )}
            {feature?.property?.description && !error && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                }}>
                    {feature.property.description}
                </span>
            )}
        </div>
    );
};

export default TextInput;
